import { Status } from '@/models/status.model';
import Vue from 'vue';
import Component from 'vue-class-component';
import EdContentSection from '@/app/private/my-content/components/ed-content-section/ed-content-section.vue';
import { CourseTemplate } from '@/course-templates/models/course-template.model';
import EdCourseTemplate from '@/course-templates/components/ed-course-template/ed-course-template.component.vue';
import EdCardsLoading from '@/components/cards/ed-cards-loading/ed-cards-loading.component.vue';
import EdCreateContentCard from '@/app/private/my-content/components/ed-create-content-card/ed-create-content-card.vue';
import { BOOKSTORE_ROUTES } from '@/app/private/bookstore/bookstore.router';
import { PRIVATE_ROUTES } from '@/app/private/private.routes';
import { PURCHASE_TYPES } from '@/course-templates/values/course-templates.values';
import { Watch } from 'vue-property-decorator';
import { LOAD_TEMPLATE_MESSAGES } from '@/core/values/app.values';
import WebSocketMessageResponse from '@/content/models/web-socket-message-response.model';

@Component({
  components: {
    EdContentSection,
    EdCourseTemplate,
    EdCreateContentCard
  }
})
export default class TemplatesView extends Vue {

  cardsLoading = EdCardsLoading;
  myTemplatesStatus: Status = new Status();
  purchasedTemplatesStatus: Status = new Status();
  sharedTemplatesStatus: Status = new Status();
  public loadingMorePurchased: boolean = false;
  public loadingMoreMyTemp: boolean = false;
  public loadingMoreShared : boolean = false;
  lastGradeLevel : any;

  get purchasedTemplates(): CourseTemplate[] {
    return this.$store.state.CourseTemplateStore.purchasedContentTemplates.content;
  }

  get myTemplates(): CourseTemplate[] {
    return this.$store.state.CourseTemplateStore.myContentTemplates.content;
  }

  get purchasedTemplatesMore(): CourseTemplate[] {
    return this.$store.state.CourseTemplateStore.purchasedContentTemplates.more;
  }

  get myTemplatesMore(): CourseTemplate[] {
    return this.$store.state.CourseTemplateStore.myContentTemplates.more;
  }

  get gradeLevelSelected() {
    return this.$store.state.CourseTemplateStore.templatesGradeLevelSelected;
  }

  get sharedTemplates(): CourseTemplate[] {
    return this.$store.state.CourseTemplateStore.sharedContentTemplates.content;
  }

  get sharedTemplatesMore(): CourseTemplate[] {
    return this.$store.state.CourseTemplateStore.sharedContentTemplates.more;
  }

  get firstSharedTemplate() {
    return this.$store.state.CourseTemplateStore.sharedContentTemplatesFirstTime;
  }

  get firstPurchaseTemplate() {
    return this.$store.state.CourseTemplateStore.purchasedContentTemplatesFirstTime;
  }

  get firstMyContentTemplate() {
    return this.$store.state.CourseTemplateStore.myCourseTemplatesFirstTime;
  }


  get sectionTemplate() {
    return [{
      name: 'purchasedTemplates',
      title: this.$i18n.t('myContent.templates.purchasedTemplates'),
      emptyMessage: this.$i18n.t('myContent.templates.emptyPTMessage'),
      emptyActionTitle: this.$i18n.t('myContent.templates.emptyPTActionTitle'),
      empty: this.purchasedTemplates.length === 0,
      loading: this.purchasedTemplatesStatus.loading,
      error: this.purchasedTemplatesStatus.error,
      load: this.purchasedTemplatesStatus.load,
      templates: this.purchasedTemplates,
      menuName: 'purchased',
      iconFooterTooltip: this.$i18n.t('myContent.templates.iconPurchasedTooltip'),
      more: this.purchasedTemplatesMore,
      loadingMore: this.loadingMorePurchased
    },{
      name: 'myTemplates',
      title: this.$i18n.t('myContent.templates.myTemplates'),
      emptyMessage: this.$i18n.t('myContent.templates.myTemplates'),
      emptyActionTitle: this.$i18n.t('myContent.templates.myTemplates'),
      empty: false,
      loading: this.myTemplatesStatus.loading,
      error: this.myTemplatesStatus.error,
      load: this.myTemplatesStatus.load,
      canCreate: true,
      templates: this.myTemplates,
      menuName: 'edition',
      iconFooterTooltip: '',
      more: this.myTemplatesMore,
      loadingMore: this.loadingMoreMyTemp
    },
    {
      name: 'sharedTemplates',
      title: this.$i18n.t('myContent.templates.sharedTemplates'),
      emptyMessage: this.$i18n.t('myContent.templates.myTemplates'),
      emptyActionTitle: this.$i18n.t('myContent.templates.myTemplates'),
      empty: this.sharedTemplates.length === 0,
      loading: this.sharedTemplatesStatus.loading,
      error: this.sharedTemplatesStatus.error,
      load: this.sharedTemplatesStatus.load,
      canCreate: false,
      templates: this.sharedTemplates,
      menuName: 'editionShared',
      iconFooterTooltip: '',
      more: this.sharedTemplatesMore,
      loadingMore: this.loadingMoreShared
    }]
  }

  async loadMore(sectionName) {
    if(sectionName == 'purchasedTemplates') {
      this.loadingMorePurchased = true;
      await this.$CourseTemplates.Store.getMyCourseTemplates(PURCHASE_TYPES.PURCHASE, this.gradeLevelSelected);
      this.loadingMorePurchased = false;
    } else if(sectionName == 'sharedTemplates') {
      this.loadingMoreShared = true;
      await this.$CourseTemplates.Store.getMyCourseTemplates(PURCHASE_TYPES.SHARED_WITH, this.gradeLevelSelected);
      this.loadingMoreShared = false;
    } else {
      this.loadingMoreMyTemp = true;
      await this.$CourseTemplates.Store.getMyCourseTemplates(PURCHASE_TYPES.OWNER, this.gradeLevelSelected);
      this.loadingMoreMyTemp = false;
    }
  }

  goToCreateTemplate() {
    this.$router.push({
      name: PRIVATE_ROUTES.CREATE_COURSE_TEMPLATE.NAME
    });
  }

  goToBookstore() {
    this.$router.push({ name: PRIVATE_ROUTES.BOOKSTORE.NAME });
  }

  async getAllTemplates(loadAgain : boolean) {
    if(loadAgain || (!this.firstPurchaseTemplate && (!this.purchasedTemplates || this.purchasedTemplates.length <= 0))) {
      this.$CourseTemplates.Store.initMyContentTemplates(PURCHASE_TYPES.PURCHASE);
      this.$CourseTemplates.Store.setFirstTimeContentTemplate(PURCHASE_TYPES.PURCHASE, true);
      await this.$CourseTemplates.Store.getMyCourseTemplates(PURCHASE_TYPES.PURCHASE, this.gradeLevelSelected);
    }
    if(loadAgain || (!this.firstSharedTemplate && (!this.sharedTemplates || this.sharedTemplates.length <= 0))) {
      this.$CourseTemplates.Store.initMyContentTemplates(PURCHASE_TYPES.SHARED_WITH);
      this.$CourseTemplates.Store.setFirstTimeContentTemplate(PURCHASE_TYPES.SHARED_WITH, true);
      await this.$CourseTemplates.Store.getMyCourseTemplates(PURCHASE_TYPES.SHARED_WITH, this.gradeLevelSelected);
    }
    if(loadAgain || (!this.firstMyContentTemplate && (!this.myTemplates || this.myTemplates.length <= 0))) {
      this.$CourseTemplates.Store.initMyContentTemplates(PURCHASE_TYPES.OWNER);
      this.$CourseTemplates.Store.setFirstTimeContentTemplate(PURCHASE_TYPES.OWNER, true);
      await this.$CourseTemplates.Store.getMyCourseTemplates(PURCHASE_TYPES.OWNER, this.gradeLevelSelected);
    }
  }

  async init() {
    this.myTemplatesStatus.setLoading();
    this.purchasedTemplatesStatus.setLoading();
    this.sharedTemplatesStatus.setLoading();
    if(this.gradeLevelSelected != null && this.gradeLevelSelected != this.lastGradeLevel) {
      this.lastGradeLevel = this.gradeLevelSelected;
      await this.getAllTemplates(true);
      setTimeout(() => {
        this.myTemplatesStatus.setLoad();
        this.purchasedTemplatesStatus.setLoad();
        this.sharedTemplatesStatus.setLoad();
      }, 2000)
    } else {
      this.myTemplatesStatus.setLoad();
      this.purchasedTemplatesStatus.setLoad();
      this.sharedTemplatesStatus.setLoad();
    }
  }

  @Watch('gradeLevelSelected', {immediate: true})
  filterGradeLevel() {
    this.init();
  }

  async mounted() {
    await this.getAllTemplates(false);
    
  }

  beforeDestroy() {
    this.$CourseTemplates.Store.setGradeLevelSelected('');
  }

}